import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/App";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { 
  Flame, Zap, Target, BookOpen, Trophy, ChevronRight,
  Play, TrendingUp, Clock, AlertTriangle, CheckCircle2, ArrowRight, HelpCircle
} from "lucide-react";
import ProgressRing from "@/components/ProgressRing";
import SubjectCard from "@/components/SubjectCard";
import ActivityHeatmap from "@/components/ActivityHeatmap";
import DailyBriefing from "@/components/DailyBriefing";
import FocusAreaCard from "@/components/FocusAreaCard";
import DailyUsageBar from "@/components/DailyUsageBar";
import AnnouncementBanner from "@/components/AnnouncementBanner";
import AnnouncementBar from "@/components/AnnouncementBar";
import DailyReviewCard from "@/components/DailyReviewCard";
import OnboardingModal from "@/components/OnboardingModal";
import ExamCountdown from "@/components/ExamCountdown";
import ReferralInline from "@/components/ReferralInline";
import TrialBanner from "@/components/TrialBanner";
import TrialUnlockBanner from "@/components/TrialUnlockBanner";
import ActivationResumeCard from "@/components/ActivationResumeCard";
import GuaranteeEligibilityCard from "@/components/GuaranteeEligibilityCard";
import SpotlightTour from "@/components/SpotlightTour";
import ActivationChecklist from "@/components/ActivationChecklist";
import { useAuth } from "@/context/AuthContext";

const ReadinessGauge = ({ score }) => {
  const getColor = (s) => {
    if (s >= 80) return { ring: "#3A7A4E", text: "text-success", bg: "bg-success/10" };
    if (s >= 60) return { ring: "#4A6E5B", text: "text-primary", bg: "bg-primary/10" };
    if (s >= 40) return { ring: "#D69228", text: "text-warning", bg: "bg-warning/10" };
    return { ring: "#D24A4A", text: "text-destructive", bg: "bg-destructive/10" };
  };
  const c = getColor(score);
  const circumference = 2 * Math.PI * 54;
  const dashOffset = circumference - (score / 100) * circumference;

  return (
    <div className="relative w-36 h-36">
      <svg viewBox="0 0 120 120" className="w-full h-full -rotate-90">
        <circle cx="60" cy="60" r="54" fill="none" stroke="currentColor" strokeWidth="8" className="text-white/5" />
        <circle cx="60" cy="60" r="54" fill="none" stroke={c.ring} strokeWidth="8" strokeLinecap="round"
          strokeDasharray={circumference} strokeDashoffset={dashOffset}
          style={{ transition: "stroke-dashoffset 1.5s ease-out" }} />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className={`text-3xl font-bold ${c.text}`}>{Math.round(score)}%</span>
        <span className="text-[10px] text-muted-foreground uppercase tracking-wider mt-0.5">Readiness</span>
      </div>
    </div>
  );
};

const Dashboard = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [progress, setProgress] = useState(null);
  const [readiness, setReadiness] = useState(null);
  const [subjects, setSubjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [checklistVisible, setChecklistVisible] = useState(false);

  useEffect(() => {
    // Hold the exam-date modal until the guided readiness setup is done/skipped,
    // so it never overlaps the activation resume card.
    if (user && user.onboarded === false && user.activation?.completed) {
      setShowOnboarding(true);
    }
  }, [user]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [progressData, subjectsData, readinessData] = await Promise.all([
          api.get("/progress"),
          api.get("/subjects"),
          api.get("/readiness")
        ]);
        setProgress(progressData.progress);
        setSubjects(subjectsData.subjects);
        setReadiness(readinessData);
      } catch (error) {
        console.error("Failed to fetch dashboard data:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const replayTour = async () => {
    try { await api.post("/tour/restart"); } catch { /* tour is best-effort */ }
    window.dispatchEvent(new Event("passadc:start-tour"));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-muted-foreground">Loading your progress...</p>
        </div>
      </div>
    );
  }

  const xpForNextLevel = progress?.next_level_xp || 500;
  const currentLevelProgress = ((500 - xpForNextLevel) / 500) * 100;

  const getStatusIcon = (status) => {
    if (status === "strong") return <CheckCircle2 className="w-3.5 h-3.5 text-success" />;
    if (status === "developing") return <TrendingUp className="w-3.5 h-3.5 text-primary" />;
    if (status === "needs_work") return <AlertTriangle className="w-3.5 h-3.5 text-warning" />;
    return <div className="w-3.5 h-3.5 rounded-full border border-border" />;
  };

  const getReadinessColor = (r) => {
    if (r >= 80) return "bg-success";
    if (r >= 60) return "bg-primary";
    if (r >= 40) return "bg-warning";
    if (r > 0) return "bg-destructive";
    return "bg-secondary";
  };

  return (
    <div data-testid="dashboard-page" className="space-y-8">
      <AnnouncementBar context="app" />
      <AnnouncementBanner />
      {showOnboarding && <OnboardingModal onClose={() => setShowOnboarding(false)} />}
      <SpotlightTour paused={showOnboarding} />
      <TrialUnlockBanner />
      <TrialBanner />
      {!checklistVisible && <ActivationResumeCard />}
      <ActivationChecklist
        onOpenOnboarding={() => setShowOnboarding(true)}
        onVisibility={setChecklistVisible}
      />
      <ExamCountdown onComplete={() => setShowOnboarding(true)} hidePrompt={checklistVisible} />
      <GuaranteeEligibilityCard />
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold">Welcome back</h1>
          <p className="text-muted-foreground">Your study path for the ADC Part 1 exam</p>
        </div>
        <div className="flex gap-3">
          <Button
            data-testid="replay-tour-btn"
            variant="ghost"
            onClick={replayTour}
            className="rounded-full text-muted-foreground hover:text-foreground"
            title="Replay the product tour"
          >
            <HelpCircle className="w-4 h-4 mr-2" /> Tour
          </Button>
          <Button data-testid="quick-practice-btn" onClick={() => navigate("/practice")} className="rounded-full">
            <Play className="w-4 h-4 mr-2" /> Start practicing
          </Button>
        </div>
      </div>

      {/* AI Daily Briefing */}
      <DailyBriefing />

      {/* Refer & earn - high-visibility growth engine */}
      <ReferralInline />

      {/* Weak-area spotlight */}
      <FocusAreaCard />

      {/* Daily Mastery Review queue */}
      <DailyReviewCard />

      {/* Daily usage meter (free users only) */}
      <DailyUsageBar />

      {/* Exam Readiness Score - Hero Section */}
      {readiness && (
        <div data-testid="readiness-score" className="glass rounded-2xl border border-border p-6 md:p-8">
          <div className="flex flex-col md:flex-row items-center gap-6 md:gap-10">
            <ReadinessGauge score={readiness.pass_probability} />
            <div className="flex-1 text-center md:text-left">
              <h2 className="text-lg font-semibold mb-1">Are you ready for the exam?</h2>
              <p className="text-sm text-muted-foreground mb-4">{readiness.verdict}</p>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
                <div>
                  <p className="text-2xl font-bold">{readiness.total_questions_answered}</p>
                  <p className="text-xs text-muted-foreground">Questions answered</p>
                </div>
                <div>
                  <p className="text-2xl font-bold">{readiness.overall_accuracy}%</p>
                  <p className="text-xs text-muted-foreground">Correct rate</p>
                </div>
                <div>
                  <p className="text-2xl font-bold">{readiness.coverage}%</p>
                  <p className="text-xs text-muted-foreground">Of bank seen</p>
                </div>
                <div>
                  <p className="text-2xl font-bold">{readiness.total_questions_available}</p>
                  <p className="text-xs text-muted-foreground">Total in bank</p>
                </div>
              </div>
            </div>
          </div>

          {/* Focus Areas */}
          {readiness.focus_areas?.length > 0 && (
            <div className="mt-6 pt-6 border-t border-border">
              <p className="text-sm font-medium text-muted-foreground mb-3">Topics to focus on</p>
              <div className="flex flex-col sm:flex-row gap-3">
                {readiness.focus_areas.map((area) => (
                  <button
                    key={area.id}
                    data-testid={`focus-area-${area.id}`}
                    onClick={() => navigate(`/practice/${area.id}`)}
                    className="flex-1 flex items-center justify-between p-3 rounded-xl bg-secondary border border-border hover:border-primary/30 transition-all text-left group"
                  >
                    <div>
                      <p className="text-sm font-medium">{area.name}</p>
                      <p className="text-xs text-muted-foreground">{area.tip}</p>
                    </div>
                    <ArrowRight className="w-4 h-4 text-muted-foreground group-hover:text-primary transition-colors flex-shrink-0 ml-2" />
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Stats Row */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="glass rounded-2xl p-5 border border-border">
          <div className="flex items-center gap-2 mb-2">
            <Flame className={`w-4 h-4 ${progress?.current_streak > 0 ? 'text-accent' : 'text-muted-foreground'}`} />
            <span className="text-xs text-muted-foreground">Study streak</span>
          </div>
          <p className="text-2xl font-bold">{progress?.current_streak || 0} <span className="text-sm text-muted-foreground">days</span></p>
          <p className="text-xs text-muted-foreground mt-1">Best: {progress?.longest_streak || 0}</p>
        </div>
        <div className="glass rounded-2xl p-5 border border-border">
          <div className="flex items-center gap-2 mb-2">
            <Target className="w-4 h-4 text-accent" />
            <span className="text-xs text-muted-foreground">Accuracy</span>
          </div>
          <p className="text-2xl font-bold">{progress?.accuracy || 0}<span className="text-sm">%</span></p>
          <p className="text-xs text-muted-foreground mt-1">{progress?.correct_answers || 0}/{progress?.questions_answered || 0}</p>
        </div>
        <div className="glass rounded-2xl p-5 border border-border">
          <div className="flex items-center gap-2 mb-2">
            <Clock className="w-4 h-4 text-success" />
            <span className="text-xs text-muted-foreground">Mock exams</span>
          </div>
          <p className="text-2xl font-bold">{readiness?.mock_exams_taken || 0}</p>
          <p className="text-xs text-muted-foreground mt-1">Avg: {readiness?.mock_exam_average || 0}%</p>
        </div>
      </div>

      {/* How to study - clear study path for ESL learners */}
      <div>
        <h2 className="text-lg font-semibold mb-1">Your study path</h2>
        <p className="text-sm text-muted-foreground mb-4">Follow these 4 steps every day. Tap any card to begin.</p>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <button
            onClick={() => navigate("/learn")}
            data-testid="path-learn-card"
            className="text-left glass rounded-2xl p-5 border border-border hover:border-primary/40 transition-all hover-lift"
          >
            <div className="flex items-center gap-2 mb-3">
              <span className="w-7 h-7 rounded-full bg-primary/20 text-primary text-xs font-bold flex items-center justify-center">1</span>
              <BookOpen className="w-5 h-5 text-primary" />
            </div>
            <p className="font-semibold mb-1">Learn</p>
            <p className="text-xs text-muted-foreground">Read short notes by topic</p>
          </button>

          <button
            onClick={() => navigate("/practice")}
            data-testid="path-practice-card"
            className="text-left glass rounded-2xl p-5 border border-border hover:border-primary/40 transition-all hover-lift"
          >
            <div className="flex items-center gap-2 mb-3">
              <span className="w-7 h-7 rounded-full bg-accent/20 text-accent text-xs font-bold flex items-center justify-center">2</span>
              <Target className="w-5 h-5 text-accent" />
            </div>
            <p className="font-semibold mb-1">Practice</p>
            <p className="text-xs text-muted-foreground">Answer real exam MCQs</p>
          </button>

          <button
            onClick={() => navigate("/cases")}
            data-testid="path-cases-card"
            className="text-left glass rounded-2xl p-5 border border-primary/30 bg-primary/5 hover:border-primary/60 transition-all hover-lift relative"
          >
            <span className="absolute top-3 right-3 text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-primary text-white">New</span>
            <div className="flex items-center gap-2 mb-3">
              <span className="w-7 h-7 rounded-full bg-primary/20 text-primary text-xs font-bold flex items-center justify-center">3</span>
              <Trophy className="w-5 h-5 text-primary" />
            </div>
            <p className="font-semibold mb-1">Clinical Cases</p>
            <p className="text-xs text-muted-foreground">Real patient scenarios</p>
          </button>

          <button
            onClick={() => navigate("/mock-exam")}
            data-testid="path-mock-card"
            className="text-left glass rounded-2xl p-5 border border-border hover:border-primary/40 transition-all hover-lift"
          >
            <div className="flex items-center gap-2 mb-3">
              <span className="w-7 h-7 rounded-full bg-success/20 text-success text-xs font-bold flex items-center justify-center">4</span>
              <Clock className="w-5 h-5 text-success" />
            </div>
            <p className="font-semibold mb-1">Mock Exam</p>
            <p className="text-xs text-muted-foreground">Test in real exam mode</p>
          </button>
        </div>
      </div>

      {/* Diagnostic CTA (only if not done yet) */}
      {!progress?.diagnostic_completed && (
        <div className="glass rounded-2xl p-6 border border-primary/30 bg-primary/5 cursor-pointer hover:border-primary/50 transition-all hover-lift" onClick={() => navigate("/diagnostic")} data-testid="diagnostic-cta">
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-primary/20"><Zap className="w-5 h-5 text-primary" /></div>
              <div>
                <p className="font-semibold">Take the 10-minute diagnostic test</p>
                <p className="text-sm text-muted-foreground">We'll find your weak topics so you study smart, not hard.</p>
              </div>
            </div>
            <ChevronRight className="w-5 h-5 text-primary flex-shrink-0" />
          </div>
        </div>
      )}

      {/* Subject Readiness Breakdown */}
      {readiness?.subject_readiness && (
        <div>
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold">Subject Readiness</h2>
            <Button variant="ghost" size="sm" onClick={() => navigate("/progress")}>
              View Details <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          </div>
          <div className="glass rounded-2xl border border-border divide-y divide-white/5">
            {readiness.subject_readiness.map((subject) => (
              <button
                key={subject.id}
                data-testid={`subject-readiness-${subject.id}`}
                onClick={() => navigate(`/practice/${subject.id}`)}
                className="w-full flex items-center gap-4 p-4 hover:bg-white/[0.02] transition-colors text-left"
              >
                {getStatusIcon(subject.status)}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between mb-1.5">
                    <span className="text-sm font-medium truncate">{subject.name}</span>
                    <span className="text-xs text-muted-foreground ml-2">{subject.readiness}%</span>
                  </div>
                  <div className="w-full h-1.5 rounded-full bg-secondary overflow-hidden">
                    <div className={`h-full rounded-full transition-all duration-700 ${getReadinessColor(subject.readiness)}`}
                      style={{ width: `${Math.max(subject.readiness, 1)}%` }} />
                  </div>
                </div>
                <div className="text-right flex-shrink-0">
                  <p className="text-xs text-muted-foreground">{subject.answered}/{subject.available}</p>
                  <p className="text-[10px] text-muted-foreground">{subject.weight}% of exam</p>
                </div>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Activity Heatmap */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold">Activity</h2>
          <span className="text-sm text-muted-foreground">Last 90 days</span>
        </div>
        <div className="glass rounded-2xl p-6 border border-border">
          <ActivityHeatmap />
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
